import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Button, Chip } from '@mui/material';

import Feature1 from '../images/managesales.png';
import Feature2 from '../images/manageinventory.jpg';
import Feature3 from '../images/managecustomer.jpg';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';

const Features = () => {
  const cards = [
    { 
      title: 'Manage Sales & Orders', 
      description: "Track every transaction in real-time with detailed reporting and lightning-fast checkout flows. Accept multiple payment methods including split payments, manage tips, process refunds, and streamline your entire sales process with speed and accuracy. Full support for dine-in, takeout, delivery, and online orders.",
      image: Feature1,
      icon: <PointOfSaleIcon />,
      color: '#FF6347',
      features: ['Real-time Tracking', 'Split Payments', 'Multiple Channels']
    },
    { 
      title: 'Manage Inventory & Menu', 
      description: "Stay in complete control of your stock with real-time inventory tracking across all locations, intelligent low-stock alerts, and automatic recipe-based deductions. Build sophisticated menus with unlimited modifiers, variants, and side-car options. Whether you have one location or many, always know what's selling and what needs restocking.",
      image: Feature2,
      icon: <InventoryIcon />,
      color: '#4CAF50',
      features: ['Live Stock Tracking', 'Low-Stock Alerts', 'Multi-location']
    },
    { 
      title: 'Manage Staff & Customers', 
      description: "Build stronger customer relationships with smart CRM tools — capture customer data, track purchase history, and offer personalized discounts or loyalty rewards. Manage your team with 6-tier role-based permissions, time punch tracking, shift management, and comprehensive performance reports to optimize your labor costs.",
      image: Feature3,
      icon: <PeopleIcon />,
      color: '#2196F3',
      features: ['Smart CRM', '6-Tier Permissions', 'Time Tracking']
    },
  ];

  return (
    <Box sx={{ 
      flexGrow: 1, 
      px: { xs: 2, sm: 4, md: 8, lg: 12 },
      py: { xs: 6, md: 10 },
      backgroundColor: '#f8f9fa'
    }}>
      {/* Section Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto' }}>
        <Typography 
          variant="h3" 
          component="h2" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.25rem' },
            color: '#1a1a1a',
            mb: 2
          }}
        >
          Everything You Need to Run Your Business
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: { xs: '0.875rem', md: '1rem' },
            color: '#666',
            lineHeight: 1.6
          }}
        >
          Powerful tools designed to streamline operations, increase efficiency, and grow your revenue
        </Typography>
      </Box>

      {/* Feature Cards */}
      <Grid container spacing={{ xs: 3, md: 4 }}>
        {cards.map((card, index) => (
          <Grid key={index} item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(0,0,0,0.06)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                  borderColor: card.color,
                  '& .feature-icon': {
                    backgroundColor: card.color,
                    color: '#fff',
                    transform: 'scale(1.1) rotate(5deg)',
                  },
                  '& .learn-more-btn': {
                    backgroundColor: card.color,
                    color: '#fff',
                  }
                },
              }}
            >
              <CardContent sx={{ 
                p: { xs: 3, md: 4 },
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Icon Badge */}
                <Box
                  className="feature-icon"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2.5,
                    backgroundColor: `${card.color}15`,
                    color: card.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    transition: 'all 0.3s ease',
                    '& svg': {
                      fontSize: 32,
                    }
                  }}
                >
                  {card.icon}
                </Box>

                {/* Image */}
                <Box
                  component="img"
                  src={card.image}
                  alt={card.title}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2,
                    mb: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                />

                {/* Title */}
                <Typography 
                  variant="h5" 
                  component="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    color: '#1a1a1a',
                    mb: 1.5,
                    lineHeight: 1.3
                  }}
                >
                  {card.title}
                </Typography>

                {/* Feature Chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {card.features.map((feature, idx) => (
                    <Chip
                      key={idx}
                      label={feature}
                      size="small"
                      sx={{
                        backgroundColor: `${card.color}10`,
                        color: card.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        '& .MuiChip-label': {
                          px: 1.5,
                        }
                      }}
                    />
                  ))}
                </Box>

                {/* Description */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    mb: 2.5,
                    flexGrow: 1
                  }}
                >
                  {card.description}
                </Typography>

                {/* CTA Button */}
                <Button
                  className="learn-more-btn"
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  href="tel:+18669920240"
                  sx={{
                    borderRadius: 2,
                    borderColor: card.color,
                    color: card.color,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    py: 1.5,
                    transition: 'all 0.3s ease',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: card.color,
                    }
                  }}
                >
                  Learn More
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Bottom CTA */}
      <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666',
            mb: 2,
            fontSize: { xs: '0.9375rem', md: '1rem' }
          }}
        >
          Ready to transform your business operations?
        </Typography>
        <Button
          variant="contained"
          size="large"
          href="tel:+18669920240"
          sx={{
            backgroundColor: '#FF6347',
            color: '#fff',
            borderRadius: 3,
            px: 4,
            py: 1.25,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 20px rgba(255, 99, 71, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#e5533d',
              boxShadow: '0 6px 30px rgba(255, 99, 71, 0.4)',
              transform: 'translateY(-2px)',
            }
          }}
        >
          Get a Free Demo
        </Button>
      </Box>
    </Box>
  );
};

export default Features;
